<template>
  <SectionHeaderBlock title="Настройки">
    <template #buttons>
      <PrimaryButton
        text="Сохранить"
        @click="saveConfiguration"
        :disabled="!canSaveConfiguration"
      />
    </template>
  </SectionHeaderBlock>

  <div :class="style.section">
    <InputStyle
      v-model="quiz.name"
      description="Название квиза"
      placeholder="Введите название квиза"
      size="M"
    />
  </div>

  <div :class="style.section">
    <InputStyle
      v-model="quiz.nameHeaderSite"
      description="Название квиза в браузере"
      placeholder="Введите название страницы"
      size="M"
    />
  </div>

  <div :class="[style.section, style.faviconBlock]">
    <span :class="style.heading">Фавикон</span>

    <div :class="style.content">
      <input
        ref="faviconInput"
        id="favicon"
        type="file"
        accept=".svg,.png,.ico,.jpg,.jpeg,.webp"
        :class="style.hiddenInput"
        @change="onFaviconSelected"
      />

      <div :class="style.faviconData">
        <ButtonUploadImage
          size="28"
          :icon="imageIcon"
          :iconTone="quiz?.faviconQuiz ? 'none' : 'tertiary'"
          :image="faviconImage"
          @click="uploadFavicon"
        />

        <p v-text="quiz.nameHeaderSite" />
      </div>

      <div :class="style.buttons">
        <SecondaryButton
          text="Загрузить фавикон"
          type="button"
          @click="uploadFavicon"
        />

        <SecondaryButton
          :icon="trashIcon"
          type="button"
          :disabled="!quiz.faviconQuiz && !faviconPreviewUrl"
          :iconTone="
            !quiz.faviconQuiz && !faviconPreviewUrl ? 'disabled' : 'accent'
          "
          @click="deleteFavicon"
        />
      </div>
    </div>
  </div>

  <div :class="style.section">
    <InputStyle
      :modelValue="standardQuizUrl"
      description="Стандартная ссылка"
      size="M"
      readOnly
    />
  </div>

  <div :class="style.section">
    <InputStyle
      :modelValue="quiz.customUrl"
      :isError="isCustomUrlAlreadyExists"
      errorText="Укажите корректную ссылку"
      positionError="top"
      description="Кастомная ссылка"
      placeholder="customUrl"
      size="M"
      @update:modelValue="updateCustomUrl"
    >
      <template #prefixInput>
        <p :class="style.prefixCustomUrl">{{ baseUrl }}</p>
      </template>
    </InputStyle>
  </div>

  <div :class="[style.section, style.switchBlock]">
    <p>А/Б-тест</p>

    <div :class="[style.switches, style.abTestButtons]">
      <div :class="style.switch">
        <SwitchComponent
          :modelValue="Boolean(quiz.ABTestBool)"
          @update:modelValue="updateStatus('ABTestBool')"
        />
      </div>

      <ButtonIcon :icon="resetIcon" @click="resetABTest" size="28" />
    </div>
  </div>

  <div :class="[style.section, style.switchBlock]">
    <p>Показывать акции в шапке</p>

    <div :class="[style.switches, style.withoutButton]">
      <div :class="style.switch">
        <span>МБ</span>

        <SwitchComponent
          :modelValue="Boolean(quiz.isActionTopMobile)"
          @update:modelValue="updateStatus('isActionTopMobile')"
        />
      </div>
    </div>
  </div>

  <div :class="[style.section, style.switchBlock]">
    <p>Нумеровать блоки справа</p>

    <div :class="style.switches">
      <div :class="style.switch">
        <span>ПК</span>

        <SwitchComponent
          :modelValue="Boolean(isNumericRightSection.pc)"
          @update:modelValue="updateStatus('isNumericRightSection', 'pc')"
        />
      </div>

      <div :class="style.switch">
        <span>МБ</span>

        <SwitchComponent
          :modelValue="Boolean(isNumericRightSection.mobile)"
          @update:modelValue="updateStatus('isNumericRightSection', 'mobile')"
        />
      </div>
    </div>
  </div>

  <div :class="[style.section, style.switchBlock]">
    <p>Точки в нумерации акций</p>

    <div :class="style.switches">
      <div :class="style.switch">
        <span>МБ</span>

        <SwitchComponent
          :modelValue="Boolean(quiz?.styleContacts?.isDot)"
          @update:modelValue="toggleDotMobileActions"
        />
      </div>
    </div>
  </div>

  <div :class="style.section">
    <InputStyle
      v-model="quiz.customScript"
      description="Кастомный HTML"
      placeholder="Введите код"
      size="M"
      :textarea="true"
      :minHeightTextArea="292"
      @keydown="handleKeyDown"
    />
  </div>
</template>

<script lang="ts">
import style from "./ConfigurationComponent.module.scss";
import {DOMAIN} from "@/http";
import {API} from "@/api/generated";
import {defineComponent} from "vue";
import {saveImgQuiz} from "@/page/quiz/scriptQuiz";
import {save} from "@/scripts/httpService";
import {getBaseUrl} from "@/page/quiz/scriptQuiz";
import {Quiz} from "../../typeQuiz";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import SwitchComponent from "@/elements/UI/input/Switch/SwitchComponent.vue";

import imageIcon from "$images/icons/button/Name=Image, Size=16, Tone=Default.svg";
import trashIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import resetIcon from "$images/icons/button/Name=Reset, Size=16, Tone=Default.svg";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import SectionHeaderBlock from "@/elements/UI/section/SectionHeaderBlock/SectionHeaderBlock.vue";
import ButtonUploadImage from "@/elements/UI/button/ButtonUploadImage/ButtonUploadImage.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";

type QuizBooleanField =
  "ABTestBool" | "isActionTopMobile" | "isNumericRightSection";

export default defineComponent({
  name: "configurationQuiz",
  emits: ["saved", "dirty-change"],

  components: {
    ButtonIcon,
    ButtonUploadImage,
    SectionHeaderBlock,
    SecondaryButton,
    SwitchComponent,
    InputStyle,
    PrimaryButton
  },

  data() {
    return {
      style,

      imageIcon,
      trashIcon,
      resetIcon,
      DOMAIN,
      baseUrl: `https://${DOMAIN}/quiz/`,
      faviconVersion: Date.now(),
      faviconPreviewUrl: "",
      statusSave: false,
      isSaving: false,
      initialQuizSnapshot: "",
      isQuizSnapshotReady: false,
      isCustomUrlAlreadyExists: false,
      customUrlErrorTimer: null as number | null
    };
  },

  async mounted() {
    window.addEventListener("keydown", this.handleSaveShortcut);

    try {
      const url = await getBaseUrl();
      this.baseUrl = this.normalizeQuizBaseUrl(url);
    } catch (error) {
      console.error("Ошибка при получении базовой ссылки квиза:", error);
      this.baseUrl = `https://${this.DOMAIN}/quiz/`;
    } finally {
      this.$nextTick(() => {
        this.setInitialQuizSnapshot();
      });
    }
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleSaveShortcut);
    this.clearCustomUrlErrorTimer();

    if (this.faviconPreviewUrl) {
      URL.revokeObjectURL(this.faviconPreviewUrl);
    }
  },

  methods: {
    handleSaveShortcut(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyS") {
        event.preventDefault();
        void this.saveConfiguration();
      }
    },

    clearCustomUrlErrorTimer(): void {
      if (this.customUrlErrorTimer === null) {
        return;
      }

      window.clearTimeout(this.customUrlErrorTimer);
      this.customUrlErrorTimer = null;
    },

    showCustomUrlDuplicateError(): void {
      this.clearCustomUrlErrorTimer();

      this.isCustomUrlAlreadyExists = true;
      this.customUrlErrorTimer = window.setTimeout(() => {
        this.isCustomUrlAlreadyExists = false;
        this.customUrlErrorTimer = null;
      }, 3000);
    },

    deleteFavicon(): void {
      this.quiz.faviconQuiz = "";

      if (this.faviconPreviewUrl) {
        URL.revokeObjectURL(this.faviconPreviewUrl);
        this.faviconPreviewUrl = "";
      }

      this.faviconVersion = Date.now();
      this.statusSave = true;

      const input = this.$refs.faviconInput as HTMLInputElement | undefined;

      if (input) {
        input.value = "";
      }
    },

    uploadFavicon(): void {
      const input = this.$refs.faviconInput as HTMLInputElement | undefined;

      if (!input) {
        return;
      }

      input.click();
    },

    toggleDotMobileActions(): void {
      if (!this.quiz.styleContacts) {
        this.quiz.styleContacts = {
          type: "both",
          isDot: false
        };
      }

      this.quiz.styleContacts.isDot = !Boolean(this.quiz.styleContacts.isDot);
    },

    normalizeQuizBaseUrl(url: string): string {
      if (!url) {
        return `https://${this.DOMAIN}/quiz/`;
      }

      const normalizedUrl = url.endsWith("/") ? url : `${url}/`;

      if (normalizedUrl.includes("/quiz/")) {
        return normalizedUrl;
      }

      return normalizedUrl.replace("/api/", "/quiz/");
    },

    handleKeyDown(e: KeyboardEvent): void {
      if (e.key !== "Tab") {
        return;
      }

      const textarea = e.target;

      if (!(textarea instanceof HTMLTextAreaElement)) {
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;

      e.preventDefault();

      if (start !== end) {
        const selected = value.slice(start, end);
        const indented = selected
          .split("\n")
          .map((line) => "  " + line)
          .join("\n");

        textarea.value = value.slice(0, start) + indented + value.slice(end);
        this.quiz.customScript = textarea.value;

        textarea.selectionStart = start;
        textarea.selectionEnd = start + indented.length;

        return;
      }

      textarea.value = value.slice(0, start) + "  " + value.slice(end);
      this.quiz.customScript = textarea.value;

      textarea.selectionStart = textarea.selectionEnd = start + 2;
    },

    async resetABTest(): Promise<void> {
      try {
        await API.analyticsReset({body: {quizId: this.quiz._id}});

        this.quiz.ABTestObj = {};
        this.statusSave = true;
      } catch (error) {
        console.error("Ошибка при сбросе А/Б-теста:", error);
      }
    },

    updateStatus(
      name: QuizBooleanField,
      platform: "mobile" | "pc" | null = null
    ): void {
      if (name === "isNumericRightSection") {
        if (!platform) {
          return;
        }

        if (
          !this.quiz.isNumericRightSection ||
          typeof this.quiz.isNumericRightSection !== "object"
        ) {
          this.quiz.isNumericRightSection = {
            pc: false,
            mobile: false
          };
        }

        this.quiz.isNumericRightSection[platform] = !Boolean(
          this.quiz.isNumericRightSection[platform]
        );

        return;
      }

      this.quiz[name] = !Boolean(this.quiz[name]);
    },

    updateCustomUrl(value: string): void {
      if (this.isCustomUrlAlreadyExists) {
        this.clearCustomUrlErrorTimer();
        this.isCustomUrlAlreadyExists = false;
      }

      this.quiz.customUrl = value.replace(/\s/g, "");
    },

    upsertCachedFile(file: File): void {
      const existingIndex = this.cachedFiles.findIndex((cachedFile: File) => {
        return cachedFile.name === file.name;
      });

      if (existingIndex !== -1) {
        this.cachedFiles.splice(existingIndex, 1, file);
        return;
      }

      this.cachedFiles.push(file);
    },

    async onFaviconSelected(event: Event): Promise<void> {
      const target = event.target as HTMLInputElement;
      const file = target?.files?.[0];

      if (!file) {
        return;
      }

      if (this.faviconPreviewUrl) {
        URL.revokeObjectURL(this.faviconPreviewUrl);
      }

      this.faviconPreviewUrl = URL.createObjectURL(file);

      const fileExtension = this.getFileExtension(file.name);
      const faviconName = `favicon.${fileExtension}`;

      const renamedFile = new File([file], faviconName, {
        type: file.type
      });

      this.quiz.faviconQuiz = faviconName;
      this.faviconVersion = Date.now();

      this.upsertCachedFile(renamedFile);

      this.statusSave = true;

      target.value = "";
    },

    async handleSaveImages(): Promise<void> {
      for (const file of this.cachedFiles) {
        await saveImgQuiz(file, false);
      }

      this.$quizStore.clearCachedFiles();
    },

    getFileExtension(fileName: string): string {
      const extension = fileName.split(".").pop();

      if (!extension) {
        return "svg";
      }

      return extension.toLowerCase();
    },

    getImg(path: string): string {
      const imageName = path || "logoWidget.svg";

      if (imageName.includes("blob:")) {
        return `url("${imageName}")`;
      }

      return `url("https://${this.DOMAIN}/api/img?name=${encodeURIComponent(imageName)}&v=${this.faviconVersion}")`;
    },

    setInitialQuizSnapshot(): void {
      this.initialQuizSnapshot = this.getQuizSnapshot();
      this.statusSave = false;
      this.isQuizSnapshotReady = true;
    },

    getQuizSnapshot(): string {
      return JSON.stringify(this.normalizeSnapshotValue(this.quiz));
    },

    normalizeSnapshotValue(value: unknown): unknown {
      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeSnapshotValue(item));
      }

      if (value && typeof value === "object") {
        const objectValue = value as Record<string, unknown>;
        const sortedObject: Record<string, unknown> = {};

        Object.keys(objectValue)
          .sort()
          .forEach((key) => {
            sortedObject[key] = this.normalizeSnapshotValue(objectValue[key]);
          });

        return sortedObject;
      }

      return value;
    },

    async saveConfiguration(): Promise<boolean> {
      if (!this.canSaveConfiguration) {
        return false;
      }

      try {
        this.isSaving = true;

        const res = await save("quiz", this.quizStr, this.quiz, {
          _id: this.quiz["_id"]
        });

        const isCustomUrlAlreadyExists =
          res?.data?.message === "Такая ссылка уже существует";

        if (isCustomUrlAlreadyExists) {
          this.showCustomUrlDuplicateError();

          return false;
        }

        this.clearCustomUrlErrorTimer();
        this.isCustomUrlAlreadyExists = false;

        if (this.cachedFiles.length > 0) {
          await this.handleSaveImages();
        }

        this.$quizStore.setQuizStartedValue(this.getQuizSnapshot());
        this.setInitialQuizSnapshot();
        this.$emit("saved");

        this.faviconVersion = Date.now();

        return true;
      } catch (error) {
        console.error("Ошибка при сохранении настроек квиза:", error);

        return false;
      } finally {
        this.isSaving = false;
      }
    }
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    standardQuizUrl(): string {
      if (!this.quiz?._id) {
        return this.baseUrl;
      }

      const normalizedBaseUrl = this.baseUrl.endsWith("/")
        ? this.baseUrl
        : `${this.baseUrl}/`;

      return `${normalizedBaseUrl}${this.quiz._id}`;
    },

    faviconImage(): string {
      if (this.faviconPreviewUrl) {
        return `url(${this.faviconPreviewUrl})`;
      }

      const favicon = this.quiz?.faviconQuiz;

      if (!favicon) {
        return "";
      }

      return this.getImg(favicon);
    },

    quizStr(): string {
      return this.$quizStore.getQuizStartedValue;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles || [];
    },
    canSaveConfiguration(): boolean {
      return (this.statusSave || this.cachedFiles.length > 0) && !this.isSaving;
    },
    isNumericRightSection() {
      return this.quiz?.isNumericRightSection || {pc: false, mobile: false};
    }
  },

  watch: {
    canSaveConfiguration(isDirty: boolean): void {
      this.$emit("dirty-change", isDirty);
    },

    quiz: {
      handler() {
        if (!this.isQuizSnapshotReady || this.isSaving) {
          return;
        }

        this.statusSave = this.getQuizSnapshot() !== this.initialQuizSnapshot;
      },
      deep: true
    },

    cachedFiles: {
      handler(newCached: File[]) {
        if (!this.isQuizSnapshotReady || this.isSaving) {
          return;
        }

        const quizChanged = this.getQuizSnapshot() !== this.initialQuizSnapshot;
        const filesChanged = newCached.length > 0;

        this.statusSave = quizChanged || filesChanged;
      },
      deep: true
    }
  }
});
</script>
